<template>
  <div class="media-container">
    <div class="media-link" :title="name" @click="emit('click')">
      <div class="media-thumbnail">
        <img
          v-if="thumbnailUrl"
          :src="thumbnailUrl"
          :alt="name"
          class="thumbnail-image"
        />
        <div v-else class="thumbnail-placeholder">
          <Spinner
            v-if="!thumbnailProcessed"
            message=""
            class="thumbnail-spinner"
          />
          <i v-else :class="placeholderIcon" class="thumbnail-icon"></i>
        </div>
        <i v-if="showPlayIcon" class="fa-solid fa-play play-icon"></i>
        <div class="buttons-container">
          <Tooltip placement="top" :disabled="breakpoints().sm">
            <template #trigger>
              <SeraButton
                icon="fa-regular fa-trash-alt"
                @click="handleDelete"
              />
            </template>
            <template #tooltip> Delete {{ mediaTypeLabel }} </template>
          </Tooltip>
          <span class="bottom-separator"> | </span>
          <Tooltip placement="top">
            <template #trigger>
              <SeraButton
                :icon="visibilityIcon"
                @click="emit('toggle-visibility')"
              />
            </template>
            <template #tooltip>
              {{ visibilityTooltip }}
            </template>
          </Tooltip>
        </div>
      </div>
    </div>
    <div class="media-name" :title="name">{{ name }}</div>
    <div class="date-created">{{ formattedDate }}</div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import SeraButton from "@/components/Button.vue";
import Spinner from "@/components/Spinner.vue";
import Tooltip from "@/components/Tooltip.vue";
import { breakpoints } from "@/mixins";

export type MediaType = "photo" | "video" | "document";

const props = withDefaults(
  defineProps<{
    thumbnailUrl?: string;
    thumbnailProcessed?: boolean;
    name?: string;
    createdAt?: string;
    customerVisible?: boolean;
    mediaType?: MediaType;
  }>(),
  {
    thumbnailUrl: "",
    thumbnailProcessed: true,
    name: "",
    createdAt: "",
    customerVisible: false,
    mediaType: "document"
  }
);

const emit = defineEmits<{
  click: [];
  delete: [];
  "toggle-visibility": [];
}>();

const mediaTypeLabel = computed(() => {
  const labels: Record<MediaType, string> = {
    photo: "Photo",
    video: "Video",
    document: "Document"
  };
  return labels[props.mediaType];
});

const placeholderIcon = computed(() => {
  const icons: Record<MediaType, string> = {
    photo: "fa-regular fa-image",
    video: "fa-solid fa-video",
    document: "fa-regular fa-file"
  };
  return icons[props.mediaType];
});

const showPlayIcon = computed(() => props.mediaType === "video");

const visibilityIcon = computed(() => {
  return props.customerVisible
    ? "fa-regular fa-eye"
    : "fa-regular fa-eye-slash";
});

const visibilityTooltip = computed(() => {
  return props.customerVisible ? "Visible to customer" : "Hidden from customer";
});

const formattedDate = computed(() => {
  if (!props.createdAt) return "";
  const datetime = window.vue_app?.config?.globalProperties?.datetime;
  if (datetime) {
    return datetime(props.createdAt);
  }
  return new Date(props.createdAt).toLocaleString();
});

function handleDelete(e: Event) {
  e.preventDefault();
  e.stopPropagation();
  emit("delete");
}
</script>

<style scoped>
.media-container {
  @apply text-center flex flex-col;
}

.media-link {
  @apply flex flex-col rounded no-underline cursor-pointer;
  color: var(--sera-color-font-body);
  border: 1px solid var(--sera-color-fields-border);
  background: var(--sera-color-neutral-gray-light-gray-100);
}

.media-thumbnail {
  @apply w-full aspect-square flex items-center justify-center rounded relative overflow-hidden;
  background: var(--sera-color-neutral-gray-light-gray-200);
}

.thumbnail-image {
  @apply w-full h-full object-contain;
  background-color: var(--sera-color-neutral-gray-light-gray-200);
}

.thumbnail-placeholder {
  @apply flex items-center justify-center w-full h-full;
}

.thumbnail-icon {
  @apply text-3xl;
  color: var(--sera-color-neutral-gray-light-gray-500);
}

.thumbnail-spinner {
  @apply scale-75;
}

.play-icon {
  @apply absolute text-3xl opacity-0 transition-opacity duration-200;
  color: rgba(255, 255, 255, 0.9);
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.media-link:hover .play-icon {
  @apply opacity-100;
}

.media-thumbnail:not(:has(.thumbnail-image)) .play-icon {
  @apply opacity-100;
  color: var(--sera-color-neutral-gray-light-gray-600);
  text-shadow: none;
}

.media-link:hover .media-thumbnail:not(:has(.thumbnail-image)) .play-icon {
  color: var(--sera-color-text-links);
}

.buttons-container {
  @apply flex flex-row items-center h-[30px] justify-evenly;
  @apply absolute bottom-0 left-0 right-0;
  background: rgba(255, 255, 255, 0.75);
}

.media-name {
  @apply text-xs overflow-hidden text-ellipsis px-0.5 mt-1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.3;
}

.date-created {
  @apply text-center;
}
</style>
